import {
  compileSkyAuthoredRig,
  sampleSkyAuthoredPose,
  skyAuthoredPoseContact,
} from './115-sky-authored-pose.mjs';
import { SKY_WORLD } from './86-sky-world-scale.mjs';
import { skyTerrainHeight } from './87-sky-terrain.mjs';

export const SKY_VESPER = Object.freeze({
  ringLimit: 3,
  pulseLimit: 2,
  ringSpeed: 22,
  ringInterval: 1.2,
  pulseSpeed: 26,
  reflectedSpeed: 36,
  ringWidth: 1.2,
  gapHalfAngle: 0.34,
  bankSeconds: 7,
});
const DURATIONS = {
  approach: 4,
  waves: 3.6,
  track: 2,
  pulse: 4.5,
  recover: 1.5,
  bank: SKY_VESPER.bankSeconds,
};
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
const pilot = (state) => ({ x: state.x, y: state.y, z: state.pilotZ ?? SKY_WORLD.pilotZ });
const angleDistance = (a, b) => Math.abs(Math.atan2(Math.sin(a - b), Math.cos(a - b)));
const xyz = (a) => ({ x: a[0], y: a[1], z: a[2] });
function toward(from, to, length) {
  const d = distance(from, to) || 1;
  return {
    x: from.x + ((to.x - from.x) * length) / d,
    y: from.y + ((to.y - from.y) * length) / d,
    z: from.z + ((to.z - from.z) * length) / d,
  };
}
function closestSegment(point, from, to) {
  const dx = to.x - from.x,
    dy = to.y - from.y,
    dz = to.z - from.z;
  const t = clamp(
    ((point.x - from.x) * dx + (point.y - from.y) * dy + (point.z - from.z) * dz) /
      (dx * dx + dy * dy + dz * dz || 1),
    0,
    1,
  );
  return distance(point, { x: from.x + dx * t, y: from.y + dy * t, z: from.z + dz * t });
}

/** Accept only the real prepared crystal geometry. Basalt or a synthetic primitive
 * cannot silently stand in for the suspended reflective source. */
export function installSkyVesperCrystalRig(state, rig) {
  if (
    !state ||
    rig?.worldId !== 'vesper-crystal' ||
    compileSkyAuthoredRig(rig) !== rig ||
    !rig.meshes.length ||
    !rig.clips.some((c) => c.name === 'Idle')
  )
    return false;
  state.authoredPropRigs ??= Object.create(null);
  state.authoredPropRigs['vesper-crystal'] = rig;
  return true;
}
/** Place the genuine crystal at an actual source facet in metres. Optional
 * desired coordinates let the approach lesson reuse the same original geometry. */
export function createSkyVesperCrystal(state, rig, side, desired = [side * 6, 17, -16]) {
  const rest = sampleSkyAuthoredPose(rig, 'Idle', 0),
    meshes = rest.meshes;
  // Pick an actual broad source triangle near the forward-facing crystal centre;
  // the targeting cue is never a box centre floating between separate blades.
  const centre = rest.bounds.min.map((n, i) => (n + rest.bounds.max[i]) / 2);
  let best = null,
    score = -Infinity;
  for (const [meshIndex, mesh] of meshes.entries())
    for (let i = 0; i < mesh.indices.length; i += 3) {
      const p = mesh.indices.slice(i, i + 3).map((id) => mesh.vertices.slice(id * 3, id * 3 + 3));
      const a = p[1].map((v, j) => v - p[0][j]),
        b = p[2].map((v, j) => v - p[0][j]);
      const n = [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]],
        area = Math.hypot(...n);
      if (area < 0.001 || Math.abs(n[2]) / area < 0.45) continue;
      const point = p[0].map((_, j) => (p[0][j] + p[1][j] + p[2][j]) / 3);
      const value =
        Math.log1p(area) - Math.hypot(point[0] - centre[0], point[1] - centre[1]) * 0.07;
      if (value > score) {
        score = value;
        best = { point, meshIndex };
      }
    }
  if (!best) return null;
  const position = desired.map((v, i) => v - best.point[i]);
  const rootTransform = { position, quaternion: [0, 0, 0, 1], scale: [1, 1, 1] };
  const pose = sampleSkyAuthoredPose(rig, 'Idle', 0, rootTransform);
  const point = xyz(desired);
  return {
    id: state.nextId++,
    kind: 'authored-prop',
    artVariant: 'vesper-crystal',
    worldId: 'vesper-crystal',
    authoredRig: rig,
    authoredPose: pose,
    rootTransform,
    vesperCrystal: true,
    side,
    hp: 1,
    maxHp: 1,
    age: 0,
    phase: 'ready',
    charge: 0,
    flash: 0,
    x: position[0],
    y: position[1],
    z: position[2],
    weakpoint: {
      ...point,
      id: `vesper-crystal-${side}`,
      radius: 2,
      criticalRadius: 1,
      exposed: false,
    },
    body: { ...point, radius: 1 },
    anatomy: { targets: [], primary: null },
  };
}

/** Bind original wing/pipe anatomy and two independently collidable crystal
 * sources. No unattended timer produces a stun, opening or damage. */
export function createSkyVesper(state, boss) {
  const rig = boss?.authoredRig,
    propRig = state.authoredPropRigs?.['vesper-crystal'];
  if (
    boss?.worldId !== 'vesper' ||
    !rig ||
    !propRig ||
    !['resonator_left', 'resonator_right', 'sonic_emitter'].every((name) =>
      rig.sockets.some((s) => s.name === name),
    ) ||
    !['Idle', 'SonicTell', 'SonicFire', 'BankLeft', 'BankRight', 'Recover', 'Defeat'].every(
      (name) => rig.clips.some((c) => c.name === name),
    )
  )
    return false;
  const props = [-1, 1].map((side) => createSkyVesperCrystal(state, propRig, side));
  if (props.some((p) => !p)) return false;
  // A direct arena start owns its initial physical altitude, like the whale's
  // cruise start. Begin above the actual crystal facets before the first live
  // frame; full journeys retain their flown position and receive no free climb.
  if (state.startMode === 'boss') {
    state.y = Math.max(...props.map((prop) => prop.weakpoint.y)) + 5;
    state.locomotion = 'fly';
  }
  const z = -42 + rig.bounds.min[2],
    y = 12 - rig.bounds.min[1] + skyTerrainHeight(0, z, state.terrainTravel ?? 0);
  boss.vesper = {
    phase: 'approach',
    age: 0,
    cycle: 0,
    rings: [],
    pulses: [],
    waveSerial: 0,
    pulseSerial: 0,
    reflections: 0,
    stuns: 0,
    bankSide: 0,
    locked: null,
    baitPoints: [],
    quietTarget: null,
    pilot: pilot(state),
    base: { x: 0, y, z },
    rootTransform: { position: [0, y, z - 48], quaternion: [0, 1, 0, 0], scale: [1, 1, 1] },
  };
  boss.authoredProps = props;
  return true;
}
function enter(state, phase, event) {
  const boss = state.boss,
    v = boss.vesper;
  v.phase = phase;
  v.age = 0;
  if (phase === 'waves') v.waveShots = 0;
  if (phase === 'pulse') v.pulsePending = true;
  boss.authoredTransition = boss.authoredPose;
  event(
    state,
    'vesper-phase',
    phase === 'track'
      ? 'LURE THE TARGETED PULSE THROUGH A CRYSTAL'
      : phase === 'bank'
        ? 'REFLECTED RESONANCE · STRIKE THE EXPOSED ORGAN PIPES'
        : 'FOLLOW THE QUIET GAP',
  );
}

/** Sonic annulus/quiet-sector contact at the same crossing plane drawn by233.
 * Relative previous/current positions prevent a fast moving ring tunnelling.
 * Empty centres and the visibly absent angular gap remain genuinely passable. */
export function skyVesperRingContact(ring, from, to, radius = SKY_WORLD.pilotContactRadius) {
  const before = from.z - ring.previousZ,
    after = to.z - ring.z;
  if (before * after > 0 && Math.min(Math.abs(before), Math.abs(after)) > radius) return false;
  const t = clamp(before / (before - after || 1), 0, 1),
    x = from.x + (to.x - from.x) * t - ring.x,
    y = from.y + (to.y - from.y) * t - ring.y;
  const r = ring.previousRadius + (ring.radius - ring.previousRadius) * t,
    radial = Math.hypot(x, y);
  if (Math.abs(radial - r) > SKY_VESPER.ringWidth / 2 + radius) return false;
  const clearance = Math.asin(Math.min(1, radius / Math.max(1, radial)));
  return angleDistance(Math.atan2(y, x), ring.gap) > SKY_VESPER.gapHalfAngle - clearance;
}

/** Active simulation time owns phases, finite waves and real source banking.
 * The returned clip and root transform are sampled once by the common skin owner. */
export function stepSkyVesper(state, dt, { event }) {
  const boss = state.boss,
    v = boss?.vesper;
  if (!v) return null;
  v.age += dt;
  if (v.phase === 'approach' && v.age >= DURATIONS.approach) enter(state, 'waves', event);
  else if (v.phase === 'waves' && v.age >= DURATIONS.waves) enter(state, 'track', event);
  else if (v.phase === 'track' && v.age >= DURATIONS.track) {
    v.locked = pilot(state);
    enter(state, 'pulse', event);
  } else if (v.phase === 'pulse' && v.age >= DURATIONS.pulse) {
    v.recoverBank = false;
    enter(state, 'recover', event);
  } else if (v.phase === 'recover' && v.age >= DURATIONS.recover) {
    v.cycle++;
    enter(state, 'waves', event);
  } else if (v.phase === 'bank' && v.age >= DURATIONS.bank) {
    v.recoverBank = true;
    enter(state, 'recover', event);
  }
  const entry = v.phase === 'approach' ? clamp(v.age / 4, 0, 1) : 1;
  v.rootTransform = {
    position: [v.base.x, v.base.y, v.base.z - 48 * (1 - entry * entry * (3 - 2 * entry))],
    quaternion: [0, 1, 0, 0],
    scale: [1, 1, 1],
  };
  let clip = 'Idle',
    seconds = v.age;
  if (v.phase === 'track') clip = 'SonicTell';
  if (v.phase === 'pulse') clip = 'SonicFire';
  if (v.phase === 'bank') clip = v.bankSide < 0 ? 'BankLeft' : 'BankRight';
  if (v.phase === 'recover' && v.recoverBank) {
    clip = v.bankSide < 0 ? 'BankLeft' : 'BankRight';
    seconds = 1.5 * (1 - clamp(v.age / DURATIONS.recover, 0, 1));
  }
  return {
    clip,
    transitionAge: v.age,
    seconds: Math.min(
      seconds,
      v.phase === 'track' ? 1.5 : v.phase === 'pulse' ? 1 : v.phase === 'bank' ? 5 : seconds,
    ),
    target: v.bankSide < 0 ? 'resonator_left' : 'resonator_right',
    exposed: v.phase === 'bank' && v.age >= 1.5,
  };
}
function emitRing(state) {
  const boss = state.boss,
    v = boss.vesper,
    origin = boss.authoredPose.sockets.sonic_emitter;
  const gap = -Math.PI / 2 + (v.waveSerial % 2 ? -0.42 : 0.42);
  v.rings.push({
    id: state.nextId++,
    x: origin.x,
    y: origin.y,
    z: origin.z,
    previousZ: origin.z,
    radius: 10,
    previousRadius: 10,
    gap,
    age: 0,
    hit: false,
  });
  if (v.rings.length > SKY_VESPER.ringLimit) v.rings.shift();
  v.waveSerial++;
}
function emitPulse(state) {
  const boss = state.boss,
    v = boss.vesper,
    origin = boss.authoredPose.sockets.sonic_emitter;
  v.pulses.push({
    id: state.nextId++,
    from: { ...origin },
    head: { ...origin },
    target: { ...v.locked },
    mode: 'outbound',
    age: 0,
    side: 0,
  });
  if (v.pulses.length > SKY_VESPER.pulseLimit) v.pulses.shift();
  v.pulseSerial++;
}
/** Advance one physical sound pulse against the nearest genuine crystal, then
 * the provided return surface. Shared by the boss and its approach lesson; no
 * damage, score or progression is granted here. Callers consume causal results. */
export function stepSkyVesperPulse(pulse, dt, props, target, surface, previous, current) {
  pulse.age += dt;
  const from = { ...pulse.head };
  const to = toward(
    from,
    pulse.mode === 'reflected' ? target : pulse.target,
    dt * (pulse.mode === 'reflected' ? SKY_VESPER.reflectedSpeed : SKY_VESPER.pulseSpeed),
  );
  let result = null;
  if (pulse.mode === 'outbound') {
    let nearest = null,
      crystal = null;
    for (const prop of props) {
      const contact = skyAuthoredPoseContact(prop.authoredPose, from, to);
      if (contact && (!nearest || contact.fraction < nearest.fraction)) {
        nearest = contact;
        crystal = prop;
      }
    }
    if (nearest) {
      pulse.head = { x: nearest.x, y: nearest.y, z: nearest.z };
      pulse.from = { ...pulse.head };
      pulse.mode = 'reflected';
      pulse.side = -crystal.side;
      crystal.flash = 1;
      crystal.charge++;
      return { reflected: crystal };
    }
    if (
      closestSegment(
        { x: 0, y: 0, z: 0 },
        { x: from.x - previous.x, y: from.y - previous.y, z: from.z - previous.z },
        { x: to.x - current.x, y: to.y - current.y, z: to.z - current.z },
      ) <
      SKY_WORLD.pilotContactRadius + 0.65
    ) {
      pulse.done = true;
      result = { hit: true };
    }
  } else {
    const contact = skyAuthoredPoseContact(surface, from, to);
    if (contact) {
      pulse.done = true;
      result = { arrived: contact };
    }
  }
  pulse.head = to;
  if (pulse.age > 7 || pulse.head.z > 20) pulse.done = true;
  return result;
}
/** Same swept annulus contact for the lesson and boss; returns only a new hit. */
export function stepSkyVesperRing(ring, dt, previous, current) {
  ring.age += dt;
  ring.previousZ = ring.z;
  ring.previousRadius = ring.radius;
  ring.z += SKY_VESPER.ringSpeed * dt;
  ring.radius += 4 * dt;
  if (ring.hit || !skyVesperRingContact(ring, previous, current)) return false;
  ring.hit = true;
  return true;
}
/** Predict the visible gap at the pilot plane, never a hidden target volume. */
export function skyVesperQuietTarget(ring, z = SKY_WORLD.pilotZ) {
  const radius = ring.radius + (4 * (z - ring.z)) / SKY_VESPER.ringSpeed;
  return { x: ring.x + Math.cos(ring.gap) * radius, y: ring.y + Math.sin(ring.gap) * radius, z };
}
function pulses(state, dt, { event, hit }) {
  const boss = state.boss,
    v = boss.vesper;
  for (const pulse of v.pulses) {
    const result = stepSkyVesperPulse(
      pulse,
      dt,
      boss.authoredProps,
      boss.authoredPose.sockets[pulse.side < 0 ? 'resonator_left' : 'resonator_right'],
      boss.authoredPose,
      v.pilot,
      pilot(state),
    );
    if (result?.reflected) {
      v.reflections++;
      event(state, 'vesper-reflection', 'CRYSTAL RESONANCE RETURNING TO VESPER');
    }
    if (result?.hit) hit?.(state, 'vesper-pulse');
    if (result?.arrived && v.phase !== 'bank') {
      v.bankSide = pulse.side;
      v.stuns++;
      enter(state, 'bank', event);
      v.rings = [];
    }
  }
  v.pulses = v.pulses.filter((p) => !p.done);
}

/** Post-pose emissions and contact use the very same source mesh/socket data
 * rendered this frame. Source crystal contact is the sole route to banking. */
export function finishSkyVesper(state, dt, { event, hit }) {
  const boss = state.boss,
    v = boss?.vesper;
  if (!v || boss.defeated) return;
  if (v.phase === 'waves') {
    const due = Math.min(3, Math.floor(v.age / SKY_VESPER.ringInterval) + 1);
    while ((v.waveShots ?? 0) < due) {
      emitRing(state);
      v.waveShots = (v.waveShots ?? 0) + 1;
    }
  }
  if (v.phase === 'pulse' && v.pulsePending) {
    emitPulse(state);
    v.pulsePending = false;
  }
  const current = pilot(state);
  for (const ring of v.rings)
    if (stepSkyVesperRing(ring, dt, v.pilot, current)) hit?.(state, 'vesper-sonic-ring');
  v.rings = v.rings.filter((r) => r.age < 5 && r.z < 20);
  pulses(state, dt, { event, hit });
  v.pilot = current;
  const origin = boss.authoredPose.sockets.sonic_emitter;
  v.baitPoints = boss.authoredProps.map((prop) => {
    prop.age += dt;
    prop.flash = Math.max(0, prop.flash - dt * 1.5);
    const target = prop.weakpoint,
      t = (SKY_WORLD.pilotZ - origin.z) / (target.z - origin.z);
    return {
      x: origin.x + (target.x - origin.x) * t,
      y: origin.y + (target.y - origin.y) * t,
      z: SKY_WORLD.pilotZ,
      side: prop.side,
    };
  });
  const next = v.rings.filter((r) => r.z < SKY_WORLD.pilotZ).sort((a, b) => b.z - a.z)[0];
  v.quietTarget = next ? skyVesperQuietTarget(next) : null;
  const names =
    v.phase === 'bank'
      ? [v.bankSide < 0 ? 'resonator_left' : 'resonator_right']
      : ['resonator_left', 'resonator_right'];
  boss.weakpoints = names.map((name) => ({
    ...boss.authoredPose.sockets[name],
    id: name,
    label: 'UNDERWING RESONATOR',
    radius: 3,
    criticalRadius: 1.5,
    exposed: v.phase === 'bank' && v.age >= 1.5,
  }));
  boss.weakpoint = boss.weakpoints[0];
  boss.anatomy.targets = boss.weakpoints;
  boss.anatomy.primary = boss.weakpoint;
  boss.authoredFlightCeiling = Math.max(
    boss.authoredFlightCeiling ?? 9,
    ...boss.weakpoints.map((p) => p.y + 5),
  );
  boss.controlHint =
    v.phase === 'bank'
      ? 'BANK EXPOSES THE ORGAN PIPES · STRIKE THE REAL RESONATOR'
      : v.phase === 'track' || v.phase === 'pulse'
        ? 'BAIT THE TARGETED PULSE THROUGH A SUSPENDED CRYSTAL'
        : 'FOLLOW THE VISIBLE QUIET GAP · CRYSTAL REFLECTION FORCES A BANK';
}

/** Only manual nearest-surface contact on the banked original resonator harms
 * Vesper. Source crystals stay available; neither shots nor Nova fake a reflection. */
export function resolveSkyVesperContact(state, target, shot, contact, effect) {
  const boss = state.boss,
    v = boss?.vesper;
  if (!v) return null;
  if (target.vesperCrystal) return { handled: true, damage: 0, quality: 'armor' };
  if (target !== boss) return null;
  if (
    effect.autonomous ||
    v.phase !== 'bank' ||
    v.age < 1.5 ||
    distance(contact, boss.weakpoint) > 4
  )
    return { handled: true, damage: 0, quality: 'armor' };
  const damage = Math.min(boss.hp, effect.damage * 2.5);
  boss.hp = Math.max(0, boss.hp - damage);
  return { handled: true, damage, quality: 'critical' };
}
